/**
 * FormDirectives — directives data-ha-* déclaratives pour les formulaires (ha/450).
 *
 * Remplace les contrôleurs de page bespoke (ex: RegistrationController) par des
 * primitives réutilisables, posées directement dans le mockup HTML.
 *
 * Directives implémentées :
 *
 *   data-ha-password-strength
 *       Posé sur un <input type="password">. À chaque saisie, met à jour :
 *         - `.rule-item` (dans le formulaire) — toggle la classe `.ok` selon
 *           3 règles fixes (≥8 caractères / une majuscule / un chiffre ou symbole).
 *         - `.strength-seg` — remplit n segments selon le score (0/1/2/4).
 *         - `.strength-label` — texte ("Faible"/"Moyen"/"Fort"), classe niveau
 *           (`weak`/`medium`/`strong`), visibilité.
 *       Le scope de recherche est le `<form>` ancêtre (sinon le document).
 *
 *   data-ha-password-toggle="<selector input>"
 *       Posé sur l'icône (ex: `.input-icon-right`). Clic bascule le `type`
 *       de l'input ciblé entre "password" et "text" + ajuste l'opacité de
 *       l'icône (1 = caché, 0.4 = visible).
 *
 *   data-ha-match="<selector other input>"
 *       Posé sur un input dont la valeur doit correspondre à un autre.
 *       Ajoute/retire la classe `.input-error` selon l'égalité ; reste sain
 *       (pas d'erreur) si la valeur de l'input courant est vide.
 *
 *   data-ha-validate-email
 *       Posé sur un <input type="email">. Affiche `.field-success` (dans le
 *       `.field` ancêtre) ssi la valeur passe la regex email.
 *
 * Idempotence : chaque directive pose un marqueur `data-ha-<name>-bound`
 * pour éviter le double-binding (cohérent avec le pattern InteractionController).
 * Reliable sur SPA via MutationObserver câblé dans `initInteractions`.
 */

import { onceI } from "./InteractionController";

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

interface StrengthLevel {
    count: number;
    cls: string;
    label: string;
}

const STRENGTH_LEVELS: StrengthLevel[] = [
    { count: 0, cls: "",       label: "" },
    { count: 1, cls: "weak",   label: "Faible" },
    { count: 2, cls: "medium", label: "Moyen" },
    { count: 4, cls: "strong", label: "Fort" },
];

function computeStrengthRules(value: string): boolean[] {
    return [
        value.length >= 8,
        /[A-Z]/.test(value),
        /[^a-zA-Z\s]/.test(value),
    ];
}

function updatePasswordStrength(input: HTMLInputElement): void {
    const scope: ParentNode = input.closest("form") || input.ownerDocument!;
    const value = input.value;
    const rules = computeStrengthRules(value);
    const score = rules.filter(Boolean).length;
    const lvl = STRENGTH_LEVELS[Math.min(score, STRENGTH_LEVELS.length - 1)];

    scope.querySelectorAll<HTMLElement>(".rule-item").forEach((item, i) => {
        item.classList.toggle("ok", !!rules[i]);
    });

    scope.querySelectorAll<HTMLElement>(".strength-seg").forEach((seg, i) => {
        seg.className = "strength-seg"
            + (i < lvl.count && lvl.cls ? " active-" + lvl.cls : "");
    });

    const lbl = scope.querySelector<HTMLElement>(".strength-label");
    if (lbl) {
        lbl.className = "strength-label" + (lvl.cls ? " " + lvl.cls : "");
        lbl.textContent = lvl.label;
        lbl.style.visibility = lvl.label ? "visible" : "hidden";
    }
}

export function initPasswordStrength(root: ParentNode = document): void {
    const inputs = root.querySelectorAll<HTMLInputElement>(
        "[data-ha-password-strength]:not([data-ha-password-strength-bound])",
    );
    inputs.forEach((input) => {
        if (!onceI(input, "data-ha-password-strength-bound")) { return; }
        input.addEventListener("input", () => updatePasswordStrength(input));
        updatePasswordStrength(input);
    });
}

export function initPasswordToggle(root: ParentNode = document): void {
    const icons = root.querySelectorAll<HTMLElement>(
        "[data-ha-password-toggle]:not([data-ha-password-toggle-bound])",
    );
    icons.forEach((icon) => {
        if (!onceI(icon, "data-ha-password-toggle-bound")) { return; }
        const sel = icon.getAttribute("data-ha-password-toggle") || "";
        let input = (sel
            ? document.querySelector(sel)
            : icon.parentElement?.querySelector("input")) as HTMLInputElement | null;
        // Forms convertis (renderer bare) : l'id html du mockup vit sur le root
        // formi, pas sur l'<input> reconstruit — descendre jusqu'à l'input.
        if (input && input.tagName !== "INPUT") {
            input = (input as unknown as HTMLElement).querySelector("input");
        }
        if (!input) { return; }
        icon.style.cursor = "pointer";
        icon.addEventListener("click", () => {
            const show = input.type === "password";
            input.type = show ? "text" : "password";
            icon.style.opacity = show ? "0.4" : "1";
        });
    });
}

export function initMatch(root: ParentNode = document): void {
    const fields = root.querySelectorAll<HTMLInputElement>(
        "[data-ha-match]:not([data-ha-match-bound])",
    );
    fields.forEach((field) => {
        if (!onceI(field, "data-ha-match-bound")) { return; }
        const sel = field.getAttribute("data-ha-match") || "";
        const other = sel ? document.querySelector<HTMLInputElement>(sel) : null;
        if (!other) { return; }
        const check = () => {
            if (field.value === "") {
                field.classList.remove("input-error");
                return;
            }
            field.classList.toggle("input-error", field.value !== other.value);
        };
        field.addEventListener("input", check);
        other.addEventListener("input", check);
    });
}

export function initValidateEmail(root: ParentNode = document): void {
    const inputs = root.querySelectorAll<HTMLInputElement>(
        "[data-ha-validate-email]:not([data-ha-validate-email-bound])",
    );
    inputs.forEach((input) => {
        if (!onceI(input, "data-ha-validate-email-bound")) { return; }
        const field = input.closest(".field");
        const success = field?.querySelector<HTMLElement>(".field-success") ?? null;
        const update = () => {
            const valid = EMAIL_RX.test(input.value);
            if (success) {
                success.style.display = valid ? "" : "none";
            }
        };
        if (success) {
            // initFieldSuccessHidden : masque jusqu'à valeur valide.
            success.style.display = "none";
        }
        input.addEventListener("input", update);
    });
}

export function initFormDirectives(root: ParentNode = document): void {
    initPasswordStrength(root);
    initPasswordToggle(root);
    initMatch(root);
    initValidateEmail(root);
}
